import ChatGroup, { ChatGroupId } from '@/chat/models/ChatGroup';
import ChatService from '@/chat/services/ChatService';
import WorkshopPlanningClient from '@/planner/client/WorkshopPlanningClient';
import SnippetType from '@/shared/constants/SnippetType';
import TagType from '@/shared/constants/TagType';
import VehicleType from '@/shared/constants/VehicleType';
import Brand from '@/shared/models/Brand';
import MobilitySolution from '@/shared/models/MobilitySolution';
import { OutletId } from '@/shared/models/Outlet';
import Snippet from '@/shared/models/Snippet';
import Tag from '@/shared/models/Tag';
import User, { UserId } from '@/shared/models/User';
import { LanguageCode } from '@/shared/models/common';
import serviceEvents, { UserLoggedIn, UserLoggedOut } from '@/shared/services/events/serviceEvents';
import { withRawResults } from '@/shared/utils/component';
import { isPresent, lookupFrom, Nullish } from '@/shared/utils/typescript';
import TireClient from '@/tires/client/TireClient';
import TireMake from '@/tires/models/TireMake';
import GeneralClient from '../client/GeneralClient';
import i18n from '../i18n';
import WorkshopTeam, { WorkshopTeamId } from '../models/WorkshopTeam';
import { ListCache } from '../utils/cache';
import AuthService from './AuthService';

class ReferenceService {
  private users = new ListCache(withRawResults(GeneralClient.loadUsers));
  private mobilitySolutions = new ListCache(withRawResults(GeneralClient.loadMobilitySolutions));
  private snippets = new ListCache(withRawResults(GeneralClient.loadSnippets));
  private tags = new ListCache(withRawResults(GeneralClient.loadTags));
  private chatGroups = new Map<ChatGroupId, ChatGroup>();
  private planningIndicators = new ListCache(withRawResults(WorkshopPlanningClient.loadIndicators));
  private brands = new ListCache(withRawResults(GeneralClient.loadBrands));
  private tireMakes = new ListCache(withRawResults(TireClient.loadMakes));

  constructor() {
    serviceEvents.on(UserLoggedIn, () => this.loadUsers());
    serviceEvents.on(UserLoggedOut, () => this.clear());
  }

  getUsers(): Array<User> {
    return this.users.getCached();
  }

  getActiveUsers(): Array<User> {
    return this.users.getCached().filter(u => u.isActive && !u.isDeleted);
  }

  getUser(id: Nullish<UserId>): User | null {
    return lookupFrom(this.getUsers(), 'user')(id);
  }

  loadUsers(): Promise<Array<User>> {
    return this.users.get();
  }

  async loadChatGroups(outletId: OutletId): Promise<void> {
    const container = await ChatService.loadInternalTargets(outletId);
    container.groups.forEach(g => this.chatGroups.set(g.id, g));
  }

  loadTireMakes(): Promise<Array<TireMake>> {
    return this.tireMakes.get();
  }

  getTireMakes(): Array<TireMake> {
    return this.tireMakes.getCached();
  }

  getChatGroup(id: Nullish<ChatGroupId>): ChatGroup | undefined {
    return isPresent(id) ? this.chatGroups.get(id) : undefined;
  }

  getWorkshopTeams(): Array<WorkshopTeam> {
    return AuthService.getCurrentUser().workshopTeams;
  }

  getWorkshopTeam(id: Nullish<WorkshopTeamId>): WorkshopTeam | undefined {
    if (isPresent(id)) return this.getWorkshopTeams().find(wt => wt.id === id);
  }

  loadTags(type: TagType, outletId: OutletId): Promise<Array<Tag>> {
    return this.tags.get(type, outletId);
  }

  getTagsByType(type: TagType, outletId: OutletId): Array<Tag> {
    return this.tags.getCached(type, outletId);
  }

  loadMobilitySolutions(outletId: OutletId): Promise<Array<MobilitySolution>> {
    return this.mobilitySolutions.get(outletId);
  }

  getMobilitySolutions(outletId: OutletId): Array<MobilitySolution> {
    return this.mobilitySolutions.getCached(outletId);
  }

  loadSnippets(type: SnippetType, language: LanguageCode = i18n.global.locale.value): Promise<Array<Snippet>> {
    return this.snippets.get(type, language);
  }

  getSnippets(type: SnippetType, language: LanguageCode = i18n.global.locale.value): Array<Snippet> {
    return this.snippets.getCached(type, language);
  }

  loadPlanningIndicators(outletId: OutletId): Promise<Array<Tag>> {
    return this.planningIndicators.get(outletId);
  }

  getPlanningIndicators(outletId: OutletId): Array<Tag> {
    return this.planningIndicators.getCached(outletId);
  }

  loadBrands(vehicleType: VehicleType): Promise<Array<Brand>> {
    return this.brands.get(vehicleType);
  }

  getBrands(vehicleType: VehicleType): Array<Brand> {
    return this.brands.getCached(vehicleType);
  }

  private clear(): void {
    this.users.clear();
    this.mobilitySolutions.clear();
    this.snippets.clear();
    this.tags.clear();
    this.chatGroups.clear();
    this.planningIndicators.clear();
    this.brands.clear();
    this.tireMakes.clear();
  }
}

export default new ReferenceService();
